/* eslint-disable @typescript-eslint/naming-convention */
import React, { memo, useCallback, useMemo } from "react";

// types
import { MROChildCart } from "@swiggy-private/cart-types";

// hooks
import { useCartDeliverySpeed } from "@cart-client/hooks/useCartDeliverySpeed";
import { useTheme } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider/use-theme";

// helpers
import { getCloudinaryUrl, isEmpty } from "@swiggy-private/js-utils";
import { ExpressIcon } from "@swiggy-private/cart-ui";
import { MRO_RX_CARD_FALLBACK_IMAGE } from "@cart-client/constants/MROConstants";
import { getMROChildCartRestaurantName } from "@cart-client/helpers/MROFoodCart";
import { getMRORestaurantDeliveryRowPresentation } from "./helpers/mro";
// icons
import { default as ChevronDownFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDownFilled/12";
import { default as BoltFilledIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/boltFilled/12";

// constants
import { DELIVERY_TYPE_NOT_AVAILABLE, SELF_DELIVERY_METHOD_LABEL } from "@cart-client/constants/CartDeliverySpeed";

// styles
import {
    RestaurantItem,
    RestaurantImageWrapper,
    RestaurantImage,
    RestaurantTextContainer,
    RestaurantNameRow,
    RestaurantName,
    DeliveryTime,
    DeliveryMethodLabel,
    DeliverySpeedIconWrapper,
} from "./styles";

interface MRORestaurantDeliveryRowProps {
    child: MROChildCart;
    isFirstItem: boolean;
    isLastItem: boolean;
    onChangeDelType: (child: MROChildCart) => void;
}

function MRORestaurantDeliveryRow({ child, isFirstItem, isLastItem, onChangeDelType }: MRORestaurantDeliveryRowProps) {
    const theme = useTheme();
    const { selectedSlaOption, deliverySpeedWidgetInfoV2, isRestaurantUnservicable } = useCartDeliverySpeed({
        cartResponse: child,
    });

    const {
        isSelfDelivery,
        deliveryMethodLabel,
        deliveryIconAsHeading,
        showBoltImage,
        deliveryTimeRange,
        selectedDeliveryType,
        isExpressDelivery,
        highlightColour,
        deliveryTimeColor,
    } = useMemo(
        () => getMRORestaurantDeliveryRowPresentation(child, selectedSlaOption, deliverySpeedWidgetInfoV2),
        [child, selectedSlaOption, deliverySpeedWidgetInfoV2],
    );

    const imageUrl = getCloudinaryUrl(child.restaurant_details?.cloudinary_image_id || MRO_RX_CARD_FALLBACK_IMAGE);

    const handleChangeDelType = useCallback(() => {
        onChangeDelType(child);
    }, [child, onChangeDelType]);

    return (
        <RestaurantItem isFirstItem={isFirstItem} isLastItem={isLastItem}>
            {!isEmpty(imageUrl) && (
                <RestaurantImageWrapper>
                    <RestaurantImage src={imageUrl} alt={getMROChildCartRestaurantName(child)} />
                </RestaurantImageWrapper>
            )}
            <RestaurantTextContainer>
                <RestaurantNameRow>
                    <RestaurantName variant="V2.body2.neutral.medium">
                        {getMROChildCartRestaurantName(child)}
                    </RestaurantName>
                    {!isEmpty(deliveryTimeRange) && !isRestaurantUnservicable && (
                        <DeliveryTime
                            variant="V2.body3.neutral.medium"
                            color={deliveryTimeColor}
                            onClick={handleChangeDelType}
                        >
                            {isExpressDelivery && (
                                <ExpressIcon type={selectedDeliveryType} selected highlightColor={highlightColour} />
                            )}
                            {showBoltImage && (
                                <BoltFilledIcon fill={theme.colors[theme.colorMode][deliveryTimeColor]} />
                            )}
                            <span>{deliveryTimeRange}</span>
                            {!isSelfDelivery && (
                                <ChevronDownFilledIcon fill={theme.colors[theme.colorMode][deliveryTimeColor]} />
                            )}
                        </DeliveryTime>
                    )}
                </RestaurantNameRow>
                {showBoltImage ? (
                    <DeliverySpeedIconWrapper>{deliveryIconAsHeading}</DeliverySpeedIconWrapper>
                ) : (
                    <>
                        {isRestaurantUnservicable && (
                            <DeliveryMethodLabel variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                                {DELIVERY_TYPE_NOT_AVAILABLE}
                            </DeliveryMethodLabel>
                        )}
                        {isSelfDelivery && !isRestaurantUnservicable && (
                            <DeliveryMethodLabel variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                                {SELF_DELIVERY_METHOD_LABEL}
                            </DeliveryMethodLabel>
                        )}
                        {!isRestaurantUnservicable && !isEmpty(deliveryMethodLabel) && !isSelfDelivery && (
                            <DeliveryMethodLabel variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                                {deliveryMethodLabel}
                            </DeliveryMethodLabel>
                        )}
                    </>
                )}
            </RestaurantTextContainer>
        </RestaurantItem>
    );
}

export default memo(MRORestaurantDeliveryRow);
